import React from 'react';
import Modal from "react-bootstrap/Modal";
import styles from "./modal.module.css";
import ArtworkImageCarousel from "../ArtworkImageCarousel";
import { Artwork, ArtworkFilterInfo, ArtworkSource } from '@/types/artworksData';
import { getProxiedImageUrl, getProxiedSoundUrl } from "../../utils/imageProxy";
import Button from '../molecules/Button';
import { VIDEO_EXT } from './buy.artwork';
import { ModelViewer } from "../3d/model.viewer";
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { rootState } from '../../store';
import { useExternalSourceState } from '../../hooks/useExternalSourceState';
import ZoomViewer from '../3d/zoom.viewer';
import type { SpaceData } from '@/types/spaceData';
import {
    descriptionParagraphs,
    ImportedMetadataDetails,
    PLACEHOLDER_IMPORT_MESSAGE,
    PSEUDO_MOM_EDIT_IMPORTED_METADATA_KEYS,
    isEuropeanaSource,
    isPlaceholderArtwork,
    resolveEuropeanaImportSourceDisplay,
    resolveEuropeanaProviderDisplay,
} from "../artworks/importedMetadata";
import { NewArtworkModal } from "./new.artwork";

type AutoPopulatePlacementMode = "singleSlot" | "spreadSlots";

type ModalProps = {
    apiClient: any;
    slot: number;
    show: boolean;
    onHide: () => void;
    artworks: Artwork[];
    currentSelected: Artwork | null;
    galleryId: string
    onArtworkChangedHandler: (slot: number, artwork: Artwork) => void;
    onListingHandler: (artworkData: any, galleryStats: any) => void;
    createArtworkHandler: () => void;
    editArtworkHandler: (selectedArtwork: Artwork) => void;
    onAutoPopulate: (data: any) => void;
    forcedExternalSources?: string[];
    includeEuropeanaSounds?: boolean;
    canUseArtworkRepositoryActions?: boolean;
    canManagePseudoMoMPillars?: boolean;
    currentWrapper?: any;
    persistedActiveArtworkIndex?: number | null;
    onArtworkWrapperChanged?: (wrapper: any) => void | Promise<void>;
    spaceData?: SpaceData | null;
    onPauseBackgroundSound?: () => void;
    onResumeBackgroundSound?: () => void;
}

const viewer = new ModelViewer();

// Persistent state for Artetra filter only (external source state lives in useExternalSourceState)
const persistentModalState = {
    filter: '',
};

// Europeana item URL regex for image cache in onChangeArtwork
const itemSearchRegexEuropeana = /\/item(\/\d+\/[^/?#]+)(?:[/?#]|$)/i;

const artworkImageCache = new Map<string, string[]>();

const normalizeBaseUrl = (value?: string): string => (value || '').trim().replace(/\/+$/, '');

const NAMED_COLOR_HEX: Record<string, string> = {
    black: '#000000',
    blue: '#0000ff',
    green: '#008000',
    red: '#ff0000',
    white: '#ffffff',
    yellow: '#ffff00',
};

const normalizeColorInputValue = (value?: string | null, fallback = '#ffffff'): string => {
    const raw = String(value || '').trim();
    if (/^#[0-9a-f]{6}$/i.test(raw)) {
        return raw;
    }
    const shortHex = raw.match(/^#([0-9a-f])([0-9a-f])([0-9a-f])$/i);
    if (shortHex) {
        return `#${shortHex[1]}${shortHex[1]}${shortHex[2]}${shortHex[2]}${shortHex[3]}${shortHex[3]}`.toLowerCase();
    }
    return NAMED_COLOR_HEX[raw.toLowerCase()] || fallback;
};

export const resolveFrontendBaseUrl = (runtimeOrigin?: string | null): string => {
    const browserOrigin = normalizeBaseUrl(
        runtimeOrigin ?? (typeof window !== 'undefined' ? window.location?.origin : '')
    );
    if (browserOrigin) {
        return browserOrigin;
    }

    const configuredUrl = normalizeBaseUrl(process.env.REACT_APP_URL);
    if (configuredUrl) {
        return configuredUrl;
    }

    return '';
}

const resolveApiBaseUrl = (): string => normalizeBaseUrl(process.env.REACT_APP_BASE_API);

const isLocalFrontendUrl = (value?: string): boolean => {
    if (!value) {
        return false;
    }

    try {
        const parsed = new URL(value);
        return parsed.protocol === 'http:' && (parsed.hostname === '127.0.0.1' || parsed.hostname === 'localhost');
    } catch (_error) {
        return false;
    }
}

const isTransientExternalArtwork = (artwork?: Artwork | null): boolean => {
    if (!artwork?.source?.sourceName) {
        return false;
    }

    return artwork.source.sourceName !== "artetra" && artwork.listed !== true && artwork.onsale !== true;
}

const isPersistedPlatformArtwork = (artwork?: Artwork | null): boolean =>
    Boolean(artwork?._id) && !isTransientExternalArtwork(artwork);

const isPlaceholderEligibleExternalArtwork = (artwork?: Artwork | null, spaceData?: SpaceData | null): boolean =>
    isPseudoMoMSpaceData(spaceData) &&
    (artwork as any)?.importable === false &&
    (
        (artwork as any)?.importUnavailableCode === "media_unavailable" ||
        String((artwork as any)?.importUnavailableReason || "").toLowerCase().includes("unavailable") ||
        String((artwork as any)?.importUnavailableReason || "").toLowerCase().includes("no direct playable")
    );

const isImportableExternalArtwork = (artwork?: Artwork | null, spaceData?: SpaceData | null): boolean =>
    (artwork as any)?.importable !== false || isPlaceholderEligibleExternalArtwork(artwork, spaceData);

const getImportUnavailableReason = (artwork?: Artwork | null): string =>
    (artwork as any)?.importUnavailableReason ||
    (artwork as any)?.source?.mediaUnavailableReason ||
    "No direct playable media URL available";

export const isPseudoMoMSpaceData = (spaceData?: Pick<SpaceData, "type" | "subtype"> | null): boolean =>
    spaceData?.type === "custom" && spaceData?.subtype === "pseudoMoM";

const getArtworkId = (artwork: any): string => String(artwork?._id || artwork || "");

const getValidSlotTarget = (value: any): number | null => {
    const target = Number(value);
    return Number.isFinite(target) && target >= 0 ? target : null;
};

export const buildPseudoMoMWrapperAfterRemoval = (currentWrapper: any, artworkIdToRemove: string, responseWrapper?: any) => {
    if (!currentWrapper?._id || !artworkIdToRemove) return null;

    const currentSlotTarget = Number(currentWrapper.slotTarget);
    const responseSlotTarget = Number(responseWrapper?.slotTarget);
    const slotTarget = Number.isFinite(responseSlotTarget) && responseSlotTarget >= 0
        ? responseSlotTarget
        : currentSlotTarget;
    const sourceWrapper = responseWrapper?._id ? {
        ...currentWrapper,
        ...responseWrapper,
        slotTarget,
    } : currentWrapper;
    const sourceArtworks = Array.isArray(currentWrapper.artworks) ? currentWrapper.artworks : [];
    const removedIndex = sourceArtworks.findIndex((artwork: any) => getArtworkId(artwork) === artworkIdToRemove);
    const remainingArtworks = sourceArtworks.filter((artwork: any) => getArtworkId(artwork) !== artworkIdToRemove);
    const currentActiveArtwork = Number(sourceWrapper.activeArtwork ?? currentWrapper.activeArtwork ?? 0);

    let nextActiveArtwork = 0;
    if (remainingArtworks.length > 0) {
        if (removedIndex < 0) {
            nextActiveArtwork = Math.min(
                Number.isFinite(currentActiveArtwork) ? currentActiveArtwork : 0,
                remainingArtworks.length - 1
            );
        } else if (currentActiveArtwork > removedIndex) {
            nextActiveArtwork = Math.max(0, currentActiveArtwork - 1);
        } else if (currentActiveArtwork === removedIndex) {
            nextActiveArtwork = Math.min(removedIndex, remainingArtworks.length - 1);
        } else {
            nextActiveArtwork = currentActiveArtwork;
        }
    }

    return {
        ...sourceWrapper,
        _id: sourceWrapper._id || currentWrapper._id,
        slotTarget,
        artworks: remainingArtworks,
        activeArtwork: nextActiveArtwork,
    };
};

export const mergePseudoMomEditedArtworkIntoWrapper = (
    currentWrapper: any,
    artworkPayload: Artwork | undefined,
    availableArtworks: Artwork[] = []
) => {
    if (!currentWrapper?._id || !artworkPayload?._id || !Array.isArray(currentWrapper.artworks)) {
        return null;
    }

    const availableArtworksById = new Map<string, Artwork>();
    currentWrapper.artworks.forEach((artwork: any) => {
        if (artwork?._id) {
            availableArtworksById.set(String(artwork._id), artwork);
        }
    });
    availableArtworks.forEach((artwork) => {
        if (artwork?._id) {
            availableArtworksById.set(String(artwork._id), artwork);
        }
    });
    availableArtworksById.set(String(artworkPayload._id), artworkPayload);

    const artworkIndex = currentWrapper.artworks.findIndex((artwork: any) =>
        String(artwork?._id || artwork) === String(artworkPayload._id)
    );
    if (artworkIndex < 0) {
        return null;
    }

    return {
        ...currentWrapper,
        artworks: currentWrapper.artworks.map((artwork: any, index: number) =>
            index === artworkIndex
                ? { ...(availableArtworksById.get(String(artwork?._id || artwork)) || {}), ...artworkPayload }
                : (availableArtworksById.get(String(artwork?._id || artwork)) || artwork)
        ),
        activeArtwork: currentWrapper.activeArtwork,
        slotTarget: currentWrapper.slotTarget,
        transforms: currentWrapper.transforms,
    };
};

const mergeAutoPopulateResponses = (responses: any[]): any => {
    return responses.reduce((aggregate, response) => ({
        status: aggregate.status && response?.status !== false,
        slotTargets: [
            ...aggregate.slotTargets,
            ...(Array.isArray(response?.slotTargets) ? response.slotTargets : []),
        ],
        listed: aggregate.listed + Number(response?.listed || 0),
        notListed: aggregate.notListed + Number(response?.notListed || 0),
        createdArtworks: [
            ...aggregate.createdArtworks,
            ...(Array.isArray(response?.createdArtworks) ? response.createdArtworks : []),
        ],
        skippedArtworks: [
            ...aggregate.skippedArtworks,
            ...(Array.isArray(response?.skippedArtworks) ? response.skippedArtworks : []),
        ],
        stats: response?.stats || aggregate.stats,
    }), {
        status: true,
        slotTargets: [],
        listed: 0,
        notListed: 0,
        createdArtworks: [],
        skippedArtworks: [],
        stats: undefined,
    });
}

export const autoPopulateWithPseudoMoMBatching = async (params: {
    apiClient: any;
    selectedExternalSource: string;
    galleryId: string;
    artworkIds: any;
    startSlot: number;
    isPseudoMoMSpace: boolean;
    placementMode: AutoPopulatePlacementMode;
}): Promise<any> => {
    const {
        apiClient,
        selectedExternalSource,
        galleryId,
        artworkIds,
        startSlot,
        isPseudoMoMSpace,
        placementMode,
    } = params;

    const pseudoMoMPlacementMode = isPseudoMoMSpace ? placementMode : undefined;
    const shouldChunkEuropeana =
        selectedExternalSource === "europeana" &&
        isPseudoMoMSpace &&
        Array.isArray(artworkIds);

    if (!shouldChunkEuropeana) {
        return apiClient.autoPopulateArtworks(
            selectedExternalSource,
            galleryId,
            artworkIds,
            startSlot,
            pseudoMoMPlacementMode
        );
    }

    const responses: any[] = [];
    let nextStartSlot = startSlot;
    for (const artwork of artworkIds) {
        const requestStartSlot = placementMode === "singleSlot" ? startSlot : nextStartSlot;
        const response = await apiClient.autoPopulateArtworks(
            selectedExternalSource,
            galleryId,
            [artwork],
            requestStartSlot,
            placementMode
        );
        if (!response?.status) {
            return response;
        }

        responses.push(response);
        if (placementMode === "spreadSlots" && Array.isArray(response.slotTargets)) {
            const usedSlots = response.slotTargets
                .map((slotTarget: any) => Number(slotTarget?.slotTarget))
                .filter((slotTarget: number) => Number.isFinite(slotTarget));
            if (usedSlots.length > 0) {
                nextStartSlot = Math.max(nextStartSlot, ...usedSlots.map((slotTarget: number) => slotTarget + 1));
            }
        }
    }

    return mergeAutoPopulateResponses(responses);
}

export const shouldSuppressSharedOembedUrl = (runtimeOrigin?: string | null, apiBase?: string | null): boolean => {
    const frontendOrigin = normalizeBaseUrl(runtimeOrigin);
    const normalizedApiBase = normalizeBaseUrl(apiBase);
    if (!frontendOrigin || !normalizedApiBase) {
        return false;
    }

    return !isLocalFrontendUrl(frontendOrigin) && isLocalFrontendUrl(normalizedApiBase);
}

const isOembedCompatibleEmbedUrl = (value?: string): boolean => {
    if (!value) {
        return false;
    }

    try {
        const parsed = new URL(value);
        return parsed.protocol === 'https:' || isLocalFrontendUrl(value);
    } catch (_error) {
        return false;
    }
}

const buildEmbedPageUrl = (artworkId?: string, frontendBaseUrl?: string): string => {
    const normalizedBaseUrl = normalizeBaseUrl(frontendBaseUrl);
    if (!artworkId || !normalizedBaseUrl) {
        return '';
    }

    return `${normalizedBaseUrl}/embed/artwork/${encodeURIComponent(artworkId)}`;
}

const buildOembedUrl = (embedUrl?: string, apiBaseUrl?: string): string => {
    const normalizedApiBaseUrl = normalizeBaseUrl(apiBaseUrl);
    if (!embedUrl || !normalizedApiBaseUrl || !isOembedCompatibleEmbedUrl(embedUrl)) {
        return '';
    }

    return `${normalizedApiBaseUrl}/oembed?url=${encodeURIComponent(embedUrl)}&format=json`;
}

export const ListArtworkModalV2 = ({ apiClient, show, onHide, artworks: _artworks,
    onArtworkChangedHandler,
    onListingHandler,
    slot, galleryId, currentSelected, createArtworkHandler, editArtworkHandler, onAutoPopulate,
    forcedExternalSources = [], includeEuropeanaSounds = false, canUseArtworkRepositoryActions = true,
    canManagePseudoMoMPillars = false, currentWrapper = null, persistedActiveArtworkIndex = null, onArtworkWrapperChanged,
    spaceData = null, onPauseBackgroundSound, onResumeBackgroundSound }: ModalProps) => {

    const isSameArtwork = React.useCallback((a?: Artwork, b?: Artwork) => {
        if (!a || !b) return false;
        if ((a as any)._id && (b as any)._id) return (a as any)._id === (b as any)._id;
        return a === b;
    }, []);

    // cache for the selected Europeana artwork 


    const [resizing, setResizing] = React.useState<boolean>(false);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [populating, setPopulating] = React.useState<boolean>(false);
    const [pseudoMoMBatchPlacementMode, setPseudoMoMBatchPlacementMode] = React.useState<AutoPopulatePlacementMode>("singleSlot");

    // Get current user from Redux store
    const user = useSelector((state: rootState) => state.auth.user);
    const externalSourceOptions = React.useMemo(() => {
        return Array.from(new Set([
            ...(user?.external_sources || []),
            ...forcedExternalSources,
        ]));
    }, [user?.external_sources, forcedExternalSources]);

    const [artworks, setArtworks] = React.useState<Artwork[]>([]);
    const [selectedArtwork, setSelectedArtwork] = React.useState<Artwork>();
    const [editingSelectedArtwork, setEditingSelectedArtwork] = React.useState<boolean>(false);
    const [stagedFlagshipArtworkId, setStagedFlagshipArtworkId] = React.useState<string | null>(null);

    const viewerRef = React.useRef<HTMLDivElement | null>(null);
    const viewerInitialized = React.useRef<boolean>(false);

    const [originalHeightCm, setOriginalHeightCm] = React.useState<number>(0);
    // Originals to detect unsaved changes
    const [originalPedestalType, setOriginalPedestalType] = React.useState<'cylinder' | 'none' | 'hexagon'>('cylinder');
    const [originalPedestalColor, setOriginalPedestalColor] = React.useState<string>('#ffffff');
    const [originalFrameType, setOriginalFrameType] = React.useState<string>('default');
    const [originalFrameColor, setOriginalFrameColor] = React.useState<string>('#000000');
    const [originalFrameThickness, setOriginalFrameThickness] = React.useState<number>(1.0);
    const [originalPasPartoutSize, setOriginalPasPartoutSize] = React.useState<number>(0);
    const [originalAffectedByLights, setOriginalAffectedByLights] = React.useState<boolean>(false);
    const [heightCm, setHeightCm] = React.useState<number>(100);

    const [filter, setFilter] = React.useState<string>(persistentModalState.filter);
    const [filteredArtworks, setFilteredArtworks] = React.useState<Artwork[]>([]);

    const externalSource = useExternalSourceState({
        apiClient,
        show,
        artetraArtworks: _artworks,
        includeEuropeanaSounds,
        setArtworks,
        setFilteredArtworks,
        setSelectedArtwork,
    });
    const {
        selectedExternalSource,
        externalSourceFilter,
        setExternalSourceFilter,
        collectionSearch,
        setCollectionSearch,
        loadingExternalSource,
        collectionSearchLoading,
        bulkListAvailable,
        onChangeExternalSource,
        searchArtworks,
        searchCollections,
    } = externalSource;
    const isPseudoMoMSpace = isPseudoMoMSpaceData(spaceData);
    const canChoosePseudoMoMBatchPlacement = isPseudoMoMSpace && selectedExternalSource !== "" && selectedExternalSource !== "artetra";
    const selectedArtworkId = selectedArtwork?._id ? String(selectedArtwork._id) : null;
    const persistedActiveArtworkIndexNumber = Number(persistedActiveArtworkIndex);
    const resolvedPersistedActiveArtworkIndex = Number.isFinite(persistedActiveArtworkIndexNumber)
        ? persistedActiveArtworkIndexNumber
        : Number(currentWrapper?.activeArtwork ?? 0);
    const persistedFlagshipArtworkId = currentWrapper?.artworks?.[resolvedPersistedActiveArtworkIndex]
        ? String(currentWrapper.artworks[resolvedPersistedActiveArtworkIndex]?._id || currentWrapper.artworks[resolvedPersistedActiveArtworkIndex])
        : null;
    const effectiveFlagshipArtworkId = stagedFlagshipArtworkId || persistedFlagshipArtworkId;
    const selectedArtworkIsPersistedFlagship = Boolean(selectedArtworkId && persistedFlagshipArtworkId && selectedArtworkId === persistedFlagshipArtworkId);
    const selectedArtworkIsStagedFlagship = Boolean(selectedArtworkId && stagedFlagshipArtworkId && selectedArtworkId === stagedFlagshipArtworkId);
    const stagedFlagshipChanged = Boolean(stagedFlagshipArtworkId && stagedFlagshipArtworkId !== persistedFlagshipArtworkId);
    const canSetFlagship = Boolean(
        isPseudoMoMSpace &&
        canManagePseudoMoMPillars &&
        currentWrapper?._id &&
        selectedArtworkId &&
        Array.isArray(currentWrapper?.artworks) &&
        currentWrapper.artworks.length > 1
    );
    const selectedArtworkIsEffectiveFlagship = Boolean(selectedArtworkId && effectiveFlagshipArtworkId && selectedArtworkId === effectiveFlagshipArtworkId);
    const isPseudoMoMEditingSelectedArtwork = Boolean(isPseudoMoMSpace && selectedArtwork && editingSelectedArtwork);
    const selectedProviderDisplay = selectedArtwork?.source
        ? (isPseudoMoMSpace && isEuropeanaSource(selectedArtwork.source)
            ? resolveEuropeanaProviderDisplay(selectedArtwork.source)
            : (resolveEuropeanaProviderDisplay(selectedArtwork.source) || selectedArtwork.source.provider || ""))
        : "";

    React.useEffect(() => {
        const allowedSources = new Set(["artetra", ...externalSourceOptions]);
        if (show && !allowedSources.has(selectedExternalSource)) {
            onChangeExternalSource("artetra");
        }
    }, [show, selectedExternalSource, externalSourceOptions, onChangeExternalSource]);

    // Loading state for "Apply Changes"
    const [savingDisplayOptions, setSavingDisplayOptions] = React.useState<boolean>(false);

    const [changingGalleryDiorama, setChangingGalleryDiorama] = React.useState<boolean>(false);
    const [shareEmbedOpen, setShareEmbedOpen] = React.useState<boolean>(false);
    const [pedestalType, setPedestalType] = React.useState<'cylinder' | 'none' | 'hexagon'>('cylinder');
    const [pedestalColor, setPedestalColor] = React.useState<string>('#000000');

    // Frame customization state
    const [frameType, setFrameType] = React.useState<string>('default');
    const [frameColor, setFrameColor] = React.useState<string>('#000000');
    const [frameThickness, setFrameThickness] = React.useState<number>(1.0);
    const [pasPartoutSize, setPasPartoutSize] = React.useState<number>(0);

    // Lighting state for image artworks
    const [affectedByLights, setAffectedByLights] = React.useState<boolean>(false);

    // Debounced version of pedestal type change handler
    const debouncedPedestalTypeChange = React.useCallback(
        React.useMemo(() => {
            let timeoutId: NodeJS.Timeout;
            return (type: string) => {
                clearTimeout(timeoutId);
                timeoutId = setTimeout(() => {
                    onChangePedestalType(type);
                }, 500); // 500ms debounce
            };
        }, []),
        []
    );


    React.useEffect(() => {
        if (!currentSelected) {
            setSelectedArtwork(null);
            setEditingSelectedArtwork(false);
            return;
        };
        setSelectedArtwork(currentSelected);
        setEditingSelectedArtwork(false);
        setHeightCm(currentSelected.artworkData.height);
        setOriginalHeightCm(currentSelected.artworkData.height);
        const initPedestalType = currentSelected.artworkData.pedestal?.type as 'cylinder' | 'none' | 'hexagon';
        const initPedestalColor = normalizeColorInputValue(currentSelected.artworkData.pedestal?.color, '#ffffff');
        const initFrameType = currentSelected.artworkData.frame?.type || 'default';
        const initFrameColor = normalizeColorInputValue(currentSelected.artworkData.frame?.color, '#000000');
        const initFrameThickness = currentSelected.artworkData.frame?.thickness || 1.0;
        const initPasPartoutSize = currentSelected.artworkData.frame?.pasPartoutSize || 0;

        setPedestalType(initPedestalType);
        setPedestalColor(initPedestalColor);
        setFrameType(initFrameType);
        setFrameColor(initFrameColor);
        setFrameThickness(initFrameThickness);
        setPasPartoutSize(initPasPartoutSize);

        // Store originals for change detection
        setOriginalPedestalType(initPedestalType);
        setOriginalPedestalColor(initPedestalColor);
        setOriginalFrameType(initFrameType);
        setOriginalFrameColor(initFrameColor);
        setOriginalFrameThickness(initFrameThickness);
        setOriginalPasPartoutSize(initPasPartoutSize);
        setOriginalAffectedByLights((currentSelected.artworkData as any).affectedByLights || false);
        setAffectedByLights((currentSelected.artworkData as any).affectedByLights || false);

        // clear artwork cache
        artworkImageCache.clear();
    }, [currentSelected]);

    React.useEffect(() => {
        setStagedFlagshipArtworkId(null);
    }, [currentWrapper?._id, persistedFlagshipArtworkId]);

    React.useEffect(() => {
        persistentModalState.filter = filter;
    }, [filter]);

    React.useEffect(() => {
        setShareEmbedOpen(false);
    }, [selectedArtwork?._id]);

    // Wrap onHide to resume background sound when modal closes (parent unmounts modal, so useEffect would not run)
    const handleHide = React.useCallback(() => {
        onResumeBackgroundSound?.();
        onHide();
    }, [onHide, onResumeBackgroundSound]);

    // Initialize 3D viewer only when selected artwork changes or when returning from edit mode.
    React.useEffect(() => {
        if (editingSelectedArtwork) {
            viewerInitialized.current = false;
            return;
        }
        if (selectedArtwork && selectedArtwork.type === "3dmodel" && !isPlaceholderArtwork(selectedArtwork) && viewerRef.current) {
            viewer.init(viewerRef.current, selectedArtwork.artworkData.dataUrl, selectedArtwork.artworkData.height);
            viewerInitialized.current = true;
        }
    }, [editingSelectedArtwork, selectedArtwork?._id, selectedArtwork?.artworkData.dataUrl, selectedArtwork?.artworkData.height]);

    const successNotify = (msg: any) => {
        toast.success(msg, {
            position: "bottom-left",
        })
    }

    const failedNotify = (msg: string) => {
        toast.error(msg, {
            position: "bottom-left",
        })
    }

    const getListingFailureMessage = (result: any): string =>
        result?.error?.message || result?.errors?.galleryQuota || result?.msg || result?.message || "Failed to list artwork";

    const onChangeHeight = (h: number) => {
        setHeightCm(h);
    }

    const onChangePedestalType = (type: string) => {
        const pedestalType = type as 'cylinder' | 'none' | 'hexagon';
        setPedestalType(pedestalType);
        // Update the selected artwork's pedestal type
        if (selectedArtwork) {
            const updatedArtwork = {
                ...selectedArtwork,
                artworkData: {
                    ...selectedArtwork.artworkData,
                    pedestal: {
                        ...selectedArtwork.artworkData.pedestal,
                        type: pedestalType
                    }
                }
            };
            setSelectedArtwork(updatedArtwork as Artwork);

            // Call the pedestal change handler to update the 3D scene
        }
    }

    const onPedestalColorChange = (color: string) => {
        setPedestalColor(color);
        if (selectedArtwork) {
            const updatedArtwork = {
                ...selectedArtwork,
                artworkData: {
                    ...selectedArtwork.artworkData,
                    pedestal: {
                        ...selectedArtwork.artworkData.pedestal,
                        color
                    }
                }
            };
            setSelectedArtwork(updatedArtwork as Artwork);
        }
        // onColorChangeHandler(color, selectedArtwork);
    }

    const onFrameTypeChange = (type: string) => {
        setFrameType(type);
        if (selectedArtwork) {
            const updatedArtwork = {
                ...selectedArtwork,
                artworkData: {
                    ...selectedArtwork.artworkData,
                    frame: {
                        type: type as "default" | "frame1" | "frame1_pprt",
                        color: frameColor,
                        thickness: frameThickness,
                        pasPartoutSize: pasPartoutSize,
                        artworkWrapperID: selectedArtwork.artworkData.frame?.artworkWrapperID
                    }
                }
            };
            setSelectedArtwork(updatedArtwork as Artwork);
        }
    }

    const onFrameColorChange = (color: string) => {
        setFrameColor(color);
        if (selectedArtwork) {
            const updatedArtwork = {
                ...selectedArtwork,
                artworkData: {
                    ...selectedArtwork.artworkData,
                    frame: {
                        type: frameType as "default" | "frame1" | "frame1_pprt",
                        color: color,
                        thickness: frameThickness,
                        pasPartoutSize: pasPartoutSize,
                        artworkWrapperID: selectedArtwork.artworkData.frame?.artworkWrapperID
                    }
                }
            };
            setSelectedArtwork(updatedArtwork as Artwork);
        }
    }

    const onFrameThicknessChange = (thickness: number) => {
        setFrameThickness(thickness);
        if (selectedArtwork) {
            const updatedArtwork = {
                ...selectedArtwork,
                artworkData: {
                    ...selectedArtwork.artworkData,
                    frame: {
                        type: frameType as "default" | "frame1" | "frame1_pprt",
                        color: frameColor,
                        thickness: thickness,
                        pasPartoutSize: pasPartoutSize,
                        artworkWrapperID: selectedArtwork.artworkData.frame?.artworkWrapperID
                    }
                }
            };
            setSelectedArtwork(updatedArtwork as Artwork);
        }
    }

    const onPasPartoutSizeChange = (size: number) => {
        setPasPartoutSize(size);
        if (selectedArtwork) {
            const updatedArtwork = {
                ...selectedArtwork,
                artworkData: {
                    ...selectedArtwork.artworkData,
                    frame: {
                        type: frameType as "default" | "frame1" | "frame1_pprt",
                        color: frameColor,
                        thickness: frameThickness,
                        pasPartoutSize: size,
                        artworkWrapperID: selectedArtwork.artworkData.frame?.artworkWrapperID
                    }
                }
            };
            setSelectedArtwork(updatedArtwork as Artwork);
        }
    }

    // Disable apply (✓) when resizing, no artwork selected, or no changes
    const canApplyResize = !!selectedArtwork && heightCm !== originalHeightCm && !resizing;
    const frameChanged = !!selectedArtwork && (selectedArtwork.type === "image" || selectedArtwork.type === "video") && (
        frameType !== originalFrameType ||
        frameColor !== originalFrameColor ||
        frameThickness !== originalFrameThickness ||
        pasPartoutSize !== originalPasPartoutSize
    );
    const pedestalChanged = !!selectedArtwork && selectedArtwork.type === "3dmodel" && (
        pedestalType !== originalPedestalType ||
        pedestalColor !== originalPedestalColor
    );
    const affectedByLightsChanged = !!selectedArtwork && (selectedArtwork.type === "image" || selectedArtwork.type === "video") && (affectedByLights !== originalAffectedByLights);
    const displayOptionsChanged = canApplyResize || frameChanged || pedestalChanged || affectedByLightsChanged;
    const canApplyChanges = !!selectedArtwork && selectedArtwork.onsale && (displayOptionsChanged || stagedFlagshipChanged) && !resizing;
    const controlsDisabled = !selectedArtwork || !selectedArtwork.onsale;

    const onAffectedByLightsChange = (affected: boolean) => {
        setAffectedByLights(affected);
        if (selectedArtwork) {
            const updatedArtwork = {
                ...selectedArtwork,
                artworkData: {
                    ...selectedArtwork.artworkData,
                    affectedByLights: affected
                }
            };
            setSelectedArtwork(updatedArtwork as Artwork);
        }
    }

    const handleClose = () => {
        // Close the modal (handleHide resumes background sound)
        handleHide();
    }

    const copyTextToClipboard = async (value: string, successMessage: string) => {
        try {
            if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(value);
            } else {
                const textArea = document.createElement('textarea');
                textArea.value = value;
                textArea.setAttribute('readonly', 'true');
                textArea.style.position = 'absolute';
                textArea.style.left = '-9999px';
                document.body.appendChild(textArea);
                textArea.select();
                document.execCommand('copy');
                document.body.removeChild(textArea);
            }

            successNotify(successMessage);
        } catch (error) {
            console.error("Failed to copy text:", error);
            failedNotify("Failed to copy link");
        }
    }

    // Note: height changes are persisted via editArtwork; no separate resize call is needed

    const onChangeArtwork = async (artwork: Artwork) => {
        // check if the artwork is from europeana
        if (artwork.source && artwork.source.sourceName === "europeana") {
            const recordId = itemSearchRegexEuropeana.exec(artwork.source.itemUrl);
            if (recordId) {
                // try to get from cache
                const cachedImages = artworkImageCache.get(recordId[1]);
                if (cachedImages && cachedImages.length > 0) {
                    artwork.source.images = cachedImages;
                } else {
                    const { images } = await apiClient.getEuropeanaItemImages(recordId[1]);
                    if (images && images.length > 0) {
                        artwork.source.images = images;
                        artworkImageCache.set(recordId[1], images);
                    }
                }
            }
        }

        setSelectedArtwork(artwork);
        setHeightCm(artwork.artworkData.height);
        setOriginalHeightCm(artwork.artworkData.height);
        setPedestalType(artwork.artworkData.pedestal?.type as 'cylinder' | 'none' | 'hexagon');
        setPedestalColor(normalizeColorInputValue(artwork.artworkData.pedestal?.color, '#ffffff'));
        setFrameType(artwork.artworkData.frame?.type || 'default');
        setFrameColor(normalizeColorInputValue(artwork.artworkData.frame?.color, '#000000'));
        setFrameThickness(artwork.artworkData.frame?.thickness || 1.0);
        setPasPartoutSize(artwork.artworkData.frame?.pasPartoutSize || 0);
        setAffectedByLights((artwork.artworkData as any).affectedByLights || false);
        setOriginalAffectedByLights((artwork.artworkData as any).affectedByLights || false);
    }

    const putOnSale = async () => {
        if (!isImportableExternalArtwork(selectedArtwork, spaceData)) {
            failedNotify(getImportUnavailableReason(selectedArtwork));
            return;
        }

        setLoading(true);

        let persistedAction: "listed" | "removed" | null = null;
        let persistedSuccessMessage = "";

        try {
            let wrapperId;
            let galleryStats;
            const _artworks = [];

            // Ensure frame data is up-to-date when listing
            const artworkToList = {
                ...selectedArtwork,
                onsale: !selectedArtwork.onsale,
                artworkData: {
                    ...selectedArtwork.artworkData,
                    frame: {
                        type: frameType as "default" | "frame1" | "frame1_pprt",
                        color: frameColor,
                        thickness: frameThickness,
                        pasPartoutSize: pasPartoutSize,
                        artworkWrapperID: selectedArtwork.artworkData.frame?.artworkWrapperID
                    },
                    affectedByLights: affectedByLights
                }
            };

            // Handle different external sources
            if (selectedArtwork.onsale || (
                selectedArtwork.source == undefined ||
                selectedArtwork.source.sourceName === "artetra" ||
                selectedArtwork.source.sourceName === "")
            ) {
                // For artetra gallery (default), use the regular putArtworkOnSale API
                const result = await apiClient.putArtworkOnSale(selectedArtwork._id, galleryId, slot, !selectedArtwork.onsale);
                wrapperId = result.wrapperId;
                if (result.status) {
                    const removingArtwork = selectedArtwork.onsale;
                    persistedAction = removingArtwork ? "removed" : "listed";
                    persistedSuccessMessage = removingArtwork
                        ? (selectedArtwork.source && selectedArtwork.source.sourceName !== "artetra" && selectedArtwork.source.sourceName !== ""
                            ? "External artwork removed from placeholder"
                            : "Artwork removed from placeholder")
                        : "Artwork listed successfully";
                    const targetListedState = !removingArtwork;
                    const normalizedResultArtwork = result.artwork
                        ? {
                            ...result.artwork,
                            onsale: targetListedState,
                            listed: targetListedState,
                        }
                        : {
                            ...selectedArtwork,
                            onsale: targetListedState,
                            listed: targetListedState,
                        };
                    const normalizedResponseWrapper = result.wrapper
                        ? {
                            ...result.wrapper,
                            artworks: Array.isArray(result.wrapper.artworks)
                                ? result.wrapper.artworks.map((artwork: any) =>
                                    getArtworkId(artwork) === getArtworkId(selectedArtwork) ? normalizedResultArtwork : artwork
                                )
                                : result.wrapper.artworks,
                        }
                        : result.wrapper;
                    const pseudoMoMWrapperAfterRemoval = removingArtwork && isPseudoMoMSpace
                        ? buildPseudoMoMWrapperAfterRemoval(currentWrapper, String(selectedArtwork._id), normalizedResponseWrapper)
                        : null;
                    const resultWrapper = pseudoMoMWrapperAfterRemoval || normalizedResponseWrapper;
                    const requestSlotTarget = getValidSlotTarget(slot) ?? slot;
                    const responseSlotTarget = getValidSlotTarget(resultWrapper?.slotTarget);
                    const shouldPreserveSceneSlotForRemoval = removingArtwork && responseSlotTarget === null;
                    const listingWrapper = shouldPreserveSceneSlotForRemoval && resultWrapper
                        ? { ...resultWrapper, slotTarget: requestSlotTarget }
                        : resultWrapper;
                    _artworks.push(normalizedResultArtwork);
                    galleryStats = result.stats;

                    const nextSelectedArtwork = normalizedResultArtwork;
                    setSelectedArtwork(nextSelectedArtwork);
                    setArtworks((prev) => prev.map((artwork) =>
                        artwork._id === nextSelectedArtwork._id ? nextSelectedArtwork : artwork
                    ));
                    setFilteredArtworks((prev) => prev.map((artwork) =>
                        artwork._id === nextSelectedArtwork._id ? nextSelectedArtwork : artwork
                    ));

                    const listingPayload = {
                        onsale: !removingArtwork,
                        listing: !removingArtwork,
                        activeArtwork: listingWrapper?.activeArtwork ?? 0,
                        artworks: listingWrapper?.artworks || _artworks,
                        name: "artwork",
                        slotTarget: listingWrapper?.slotTarget ?? requestSlotTarget,
                        transforms: listingWrapper?.transforms || {
                            position: {
                                x: 0,
                                y: 0,
                                z: 0
                            },
                            rotation: {
                                x: 0,
                                y: 0,
                                z: 0
                            },
                            scale: {
                                x: 1,
                                y: 1,
                                z: 1
                            }
                        },
                        _id: listingWrapper?._id || wrapperId,
                        artwork: normalizedResultArtwork,
                        wrapper: listingWrapper
                    };

                    await Promise.resolve(onListingHandler(listingPayload, galleryStats) as any);
                    if (removingArtwork && isPseudoMoMSpace && pseudoMoMWrapperAfterRemoval) {
                        const nextArtwork = pseudoMoMWrapperAfterRemoval.artworks?.[pseudoMoMWrapperAfterRemoval.activeArtwork];
                        if (nextArtwork) {
                            await onChangeArtwork(nextArtwork);
                        }
                    }
                    successNotify(persistedSuccessMessage);
                } else {
                    failedNotify(getListingFailureMessage(result));
                }
            } else if (selectedArtwork && !selectedArtwork.onsale && selectedArtwork.source) {
                // For europeana source, use listExternalArtwork API
                const result = await apiClient.listExternalArtwork(selectedExternalSource, artworkToList, galleryId, slot);
                wrapperId = result.wrapperId;
                if (result.status) {
                    persistedAction = "listed";
                    persistedSuccessMessage = result.message || "External artwork listed successfully";
                    if (result.artwork) {
                        _artworks.push(result.artwork)
                    }
                    galleryStats = result.stats;

                    setSelectedArtwork(result.artwork || {
                        ...selectedArtwork,
                        onsale: true,
                        listed: true
                    })
                    // Same action as before to show artwork on placeholder
                    await Promise.resolve(onListingHandler({
                        onsale: !selectedArtwork.onsale,
                        listing: !selectedArtwork.onsale,
                        activeArtwork: result.wrapper?.activeArtwork ?? 0,
                        artworks: result.wrapper?.artworks || _artworks,
                        name: "artwork",
                        slotTarget: result.wrapper?.slotTarget ?? slot,
                        transforms: result.wrapper?.transforms || {
                            position: {
                                x: 0,
                                y: 0,
                                z: 0
                            },
                            rotation: {
                                x: 0,
                                y: 0,
                                z: 0
                            },
                            scale: {
                                x: 1,
                                y: 1,
                                z: 1
                            }
                        },
                        _id: result.wrapper?._id || wrapperId,
                        wrapper: result.wrapper
                    }, galleryStats) as any);
                    successNotify(persistedSuccessMessage);
                } else {
                    failedNotify(getListingFailureMessage(result));
                }
            }

        } catch (error) {
            console.error("Error listing artwork:", error);
            if (persistedAction === "listed") {
                failedNotify("Artwork was listed, but could not be displayed. Refresh to retry.");
            } else if (persistedAction === "removed") {
                failedNotify("Artwork was removed, but the gallery could not refresh. Refresh to retry.");
            } else {
                failedNotify(getListingFailureMessage((error as any)?.response?.data));
            }
        } finally {
            setLoading(false);
        }
    }

    const sortBy = (sort: string) => {
        function compare(a: Artwork, b: Artwork) {
            if (a[sort] === undefined) a[sort] = "";
            if (b[sort] === undefined) b[sort] = "";

            if (a[sort] === "" && b[sort] !== "") return 1;
            if (b[sort] === "" && a[sort] !== "") return -1;

            if (a[sort] < b[sort]) {
                return -1;
            }
            if (a[sort] > b[sort]) {
                return 1;
            }
            return 0;
        }
        setFilteredArtworks(artworks.sort(compare).filter(a => !a.onsale));
    }

    const frontendBaseUrl = resolveFrontendBaseUrl();
    const apiBaseUrl = resolveApiBaseUrl();
    const shareBlockedByConfiguration = shouldSuppressSharedOembedUrl(frontendBaseUrl, apiBaseUrl);
    const isEmbedShareAvailable = Boolean(selectedArtwork && isPersistedPlatformArtwork(selectedArtwork));
    const embedPageUrl = shareBlockedByConfiguration ? '' : buildEmbedPageUrl(selectedArtwork?._id, frontendBaseUrl);
    const oembedUrl = shareBlockedByConfiguration ? '' : buildOembedUrl(embedPageUrl, apiBaseUrl);
    const shareEmbedTooltip = !selectedArtwork
        ? "Select an artwork to share"
        : !isEmbedShareAvailable
            ? "Available once the artwork is persisted in Artetra"
            : shareBlockedByConfiguration
                ? "Share links are disabled because this environment points at a localhost API base"
            : "Copy embed links for this artwork";
    const oembedUnavailableReason = !isEmbedShareAvailable
        ? "Persist this artwork in Artetra before generating share links."
        : shareBlockedByConfiguration
            ? "Share links are disabled because this build is pointing at a localhost API base from a shared environment."
        : !oembedUrl
            ? "oEmbed requires an HTTPS frontend URL or local localhost dev URL in this environment."
            : "";

    const onSaveFrameSettings = async () => {
        if (selectedArtwork && selectedArtwork._id && (selectedArtwork.type === "image" || selectedArtwork.type === "video")) {
            try {
                const updatedArtwork: Artwork = {
                    ...selectedArtwork,
                    artworkData: {
                        ...selectedArtwork.artworkData,
                        height: heightCm,
                        frame: {
                            type: frameType as "default" | "frame1" | "frame1_pprt",
                            color: frameColor,
                            thickness: frameThickness,
                            pasPartoutSize: pasPartoutSize,
                            artworkWrapperID: selectedArtwork.artworkData.frame?.artworkWrapperID
                        },
                        affectedByLights: selectedArtwork.artworkData.affectedByLights
                    }
                };
                const { status } = await apiClient.editArtwork(selectedArtwork._id, updatedArtwork);
                if (!status) {
                    return false;
                }

                // update originals to reflect saved state
                setOriginalFrameType(frameType);
                setOriginalFrameColor(frameColor);
                setOriginalFrameThickness(frameThickness);
                setOriginalPasPartoutSize(pasPartoutSize);
                setOriginalAffectedByLights(affectedByLights);

                return true;
            } catch (error) {
                console.error("Error saving frame data:", error);
                return false;
            }
        } else if (selectedArtwork && selectedArtwork._id && selectedArtwork.type === "sound") {
            try {
                const updatedArtwork: Artwork = {
                    ...selectedArtwork,
                    artworkData: {
                        ...selectedArtwork.artworkData,
                        height: heightCm
                    }
                };

                const { status } = await apiClient.editArtwork(selectedArtwork._id, updatedArtwork);
                if (!status) {
                    failedNotify("Failed to save sound settings");
                    return;
                }
                successNotify("Sound settings saved successfully");
            } catch (error) {
                console.error("Error saving sound data:", error);
                failedNotify("Failed to save sound settings");
            }
        }
    }

    const onResizeSettings = async () => {
        try {
            let updatedArtwork = {}

            if (selectedArtwork.type === "3dmodel") {
                const currentPedestal = selectedArtwork.artworkData.pedestal || {
                    type: "none",
                    diameter: 1,
                    color: "white"
                };

                updatedArtwork = {
                    ...selectedArtwork,
                    artworkData: {
                        ...selectedArtwork.artworkData,
                        pedestal: {
                            type: currentPedestal.type,
                            diameter: currentPedestal.diameter,
                            color: currentPedestal.color,
                        },
                        height: heightCm
                    }
                }
            } else {
                updatedArtwork = {
                    ...selectedArtwork,
                    artworkData: {
                        ...selectedArtwork.artworkData,
                        height: heightCm
                    }
                }
            }

            const { status } = await apiClient.editArtwork(selectedArtwork._id, updatedArtwork);
            return status;
        } catch (error) {
            console.log(error);
            return false;
        }
    }

    const onSavePedestalSettings = async () => {
        if (selectedArtwork && selectedArtwork.type === "3dmodel") {
            try {
                // Get current pedestal data
                const currentPedestal = selectedArtwork.artworkData.pedestal;

                // Create updated artwork with pedestal data (excluding mesh)
                const updatedArtwork: Artwork = {
                    ...selectedArtwork,
                    artworkData: {
                        ...selectedArtwork.artworkData,
                        height: heightCm,
                        pedestal: {
                            type: pedestalType as 'cylinder' | 'none' | 'hexagon',
                            diameter: currentPedestal?.diameter || 1.0,
                            color: pedestalColor
                            // Note: mesh is excluded from database save
                        },
                        frame: undefined,
                    }
                };

                // Save to database
                const { status } = await apiClient.editArtwork(selectedArtwork._id, updatedArtwork);
                if (!status) {
                    return false;
                }
                // update originals to reflect saved state
                setOriginalPedestalType(pedestalType);
                setOriginalPedestalColor(pedestalColor);

                return true;
            } catch (error) {
                console.error("Error saving pedestal data:", error);
                return false;
            }
        }
    }

    const autoPopulateHandler = async () => {
        try {
            const artworksToList: Artwork[] = [];
            let artworkIds: any = [];

            if (selectedExternalSource === "" || selectedExternalSource === "artetra") {
                artworks.forEach(artwork => {
                    if (artwork.onsale) {
                        return;
                    }

                    let includeArtwork = false;

                    // Apply text filter
                    if (filter !== "") {
                        if (artwork.artistName && artwork.artistName.toLowerCase().includes(filter.toLowerCase())) {
                            includeArtwork = true;
                        } else if (artwork.title && artwork.title.toLowerCase().includes(filter.toLowerCase())) {
                            includeArtwork = true;
                        }
                    } else {
                        includeArtwork = true;
                    }

                    if (includeArtwork) {
                        artworksToList.push(artwork);
                    }
                })

                artworkIds = artworksToList.reduce((acc: string, a) => acc += `${a._id}, `, '');
                artworkIds = artworkIds.replace(/,\s*$/, "")
            } else if (selectedExternalSource !== "artetra") {
                for (const _artwork of artworks) {
                    const artworkToList = {
                        ..._artwork,
                        onsale: !_artwork.onsale,
                        artworkData: {
                            ..._artwork.artworkData,
                            frame: {
                                type: frameType as "default" | "frame1" | "frame1_pprt",
                                color: frameColor,
                                thickness: frameThickness,
                                pasPartoutSize: pasPartoutSize,
                                artworkWrapperID: _artwork.artworkData.frame?.artworkWrapperID
                            },
                            affectedByLights: affectedByLights
                        }
                    };
                    artworkIds.push(artworkToList);
                }
            }

            setPopulating(true);
            const populateStatus = await autoPopulateWithPseudoMoMBatching({
                apiClient,
                selectedExternalSource,
                galleryId,
                artworkIds,
                startSlot: slot,
                isPseudoMoMSpace,
                placementMode: pseudoMoMBatchPlacementMode,
            });
            onAutoPopulate(populateStatus);
        } catch (error) {
            console.log(error);
        } finally {
            setPopulating(false);
        }
    }

    const setAsGalleryThumbnail = async () => {
        try {
            setChangingGalleryDiorama(true);
            const status = await apiClient.changeGalleryDiorama(galleryId, selectedArtwork._id);

            if (status) {
                successNotify("Set as diorama successfully")
            } else {
                failedNotify("Failed to set as diorama");
            }
        } catch (error) {
            console.log(error);
            failedNotify("Failed to set as diorama");
        } finally {
            setChangingGalleryDiorama(false);
        }
    }

    const setSelectedArtworkAsFlagship = async () => {
        if (!canSetFlagship || !selectedArtworkId || selectedArtworkIsEffectiveFlagship) return;
        if (selectedArtworkId === persistedFlagshipArtworkId) {
            setStagedFlagshipArtworkId(null);
            return;
        }
        setStagedFlagshipArtworkId(selectedArtworkId);
    }

    const notifyArtworkUpdate = (artworkPayload: Artwork): boolean => {
        if (
            isPseudoMoMSpace &&
            onArtworkWrapperChanged
        ) {
            const updatedWrapper = mergePseudoMomEditedArtworkIntoWrapper(currentWrapper, artworkPayload, artworks);
            if (updatedWrapper) {
                onArtworkWrapperChanged(updatedWrapper);
                return true;
            }
        }

        onArtworkChangedHandler(slot, artworkPayload);
        return false;
    }

    const onEditedArtworkSaved = (edited: boolean, artworkPayload: Artwork | undefined) => {
        if (!artworkPayload) {
            setEditingSelectedArtwork(false);
            return;
        }

        setEditingSelectedArtwork(false);
        setSelectedArtwork(artworkPayload);
        setArtworks((prev) => prev.map((artwork) => artwork._id === artworkPayload._id ? artworkPayload : artwork));
        if (edited) {
            notifyArtworkUpdate(artworkPayload);
        }
    }

    const onSaveDisplayOptions = async () => {
        setSavingDisplayOptions(true);
        try {
            if (!selectedArtwork || !selectedArtwork._id) {
                return
            }

            let status = true;
            let savedSomething = false;
            let savedDisplayArtwork: Artwork | null = null;
            if (displayOptionsChanged) {
                if (selectedArtwork.type === "image" || selectedArtwork.type === "video" || selectedArtwork.type === "sound") {

                    if (frameChanged || affectedByLightsChanged) {
                        status = await onSaveFrameSettings();
                    } else if (canApplyResize) {
                        status = await apiClient.resizeArtwork(selectedArtwork._id, heightCm);
                    }
                } else if (selectedArtwork.type === "3dmodel") {
                    if (pedestalChanged) {
                        status = await onSavePedestalSettings();
                    } else if (canApplyResize) {
                        status = await onResizeSettings();
                    }
                }

                if (!status) {
                    failedNotify("Failed To Save Display Option Changes");
                    return;
                }

                if (canApplyResize) {
                    setOriginalHeightCm(heightCm);
                }
                savedSomething = true;
            }

            if (status && displayOptionsChanged) {
                let updatedArtwork: Artwork = {
                    ...selectedArtwork,
                    artworkData: {
                        ...selectedArtwork.artworkData,
                        height: heightCm
                    }
                };

                if (selectedArtwork.type === "image" || selectedArtwork.type === "video") {
                    updatedArtwork = {
                        ...updatedArtwork,
                        artworkData: {
                            ...updatedArtwork.artworkData,
                            frame: {
                                type: frameType as "default" | "frame1" | "frame1_pprt",
                                color: frameColor,
                                thickness: frameThickness,
                                pasPartoutSize: pasPartoutSize,
                                artworkWrapperID: selectedArtwork.artworkData.frame?.artworkWrapperID
                            },
                            affectedByLights: affectedByLights
                        }
                    };
                } else if (selectedArtwork.type === "3dmodel") {
                    const currentPedestal = selectedArtwork.artworkData.pedestal || {
                        type: "none" as "none" | "cylinder" | "hexagon",
                        diameter: 1,
                        color: "white"
                    };
                    updatedArtwork = {
                        ...updatedArtwork,
                        artworkData: {
                            ...updatedArtwork.artworkData,
                            pedestal: {
                                ...currentPedestal,
                                type: pedestalType as "cylinder" | "none" | "hexagon",
                                color: pedestalColor
                            }
                        }
                    };
                }
                setSelectedArtwork(updatedArtwork);
                setArtworks((prev) => prev.map((artwork) => artwork._id === updatedArtwork._id ? updatedArtwork : artwork));
                savedDisplayArtwork = updatedArtwork;
                notifyArtworkUpdate(updatedArtwork);
            }

            if (status && stagedFlagshipChanged && stagedFlagshipArtworkId && currentWrapper?._id) {
                const response = await apiClient.setArtworkWrapperActiveArtwork(galleryId, currentWrapper._id, stagedFlagshipArtworkId);
                if (response?.status) {
                    const fallbackActiveArtwork = currentWrapper.artworks.findIndex((artwork: any) =>
                        String(artwork?._id || artwork) === String(stagedFlagshipArtworkId)
                    );
                    const updatedWrapper = response.data?.wrapper || response.wrapper || {
                        ...currentWrapper,
                        activeArtwork: fallbackActiveArtwork >= 0 ? fallbackActiveArtwork : resolvedPersistedActiveArtworkIndex
                    };
                    const wrapperWithDisplayChanges = savedDisplayArtwork
                        ? mergePseudoMomEditedArtworkIntoWrapper(updatedWrapper, savedDisplayArtwork, artworks) || updatedWrapper
                        : updatedWrapper;
                    onArtworkWrapperChanged?.(wrapperWithDisplayChanges);
                    setStagedFlagshipArtworkId(null);
                    savedSomething = true;
                } else {
                    failedNotify(response?.error?.message || response?.msg || "Failed to update flagship artwork");
                    return;
                }
            }

            if (savedSomething) {
                successNotify("Saved Display Option Changes");
            }
        } catch (error) {
            console.log(error);
            failedNotify("Failed To Save Display Option Changes");
        } finally {
            setSavingDisplayOptions(false);
        }
    }

    return (<>
        <Modal
            show={show}
            onHide={handleHide}
            size="xl"
            centered
            backdrop="static"
            dialogClassName={styles.wideModal}
            className={styles.noScroll}
        >
            <Modal.Body className={styles.background}>
                <div className={styles.modalRoot}>
                    <div className={styles.container}>

                        <div className={styles['left-panel']}>
                            <div className={styles['panel-header']}>
                                <h2 className={styles['panel-title']}>Artworks list</h2>

                                <div className={styles['source-section']} data-tooltip="Switch between your Artetra artworks repository and any other external sources you have enabled in on your profile page.">
                                    <label className={styles['source-label']}>Source</label>
                                    <select value={selectedExternalSource} onChange={(e) => onChangeExternalSource(e.target.value)}>
                                        <option value="artetra">Artetra</option>
                                        {externalSourceOptions.map((source: string, index: number) => (
                                            <option key={index} value={source}>{source.charAt(0).toUpperCase() + source.slice(1)}</option>
                                        ))}
                                    </select>
                                </div>

                                {selectedExternalSource === "artetra" &&
                                    <div className={styles['filter-section']}>
                                        <input
                                            type="text"
                                            className={styles['filter-input']}
                                            placeholder="Filter artworks..."
                                            value={filter}
                                            onChange={e => {
                                                const newFilter = e.target.value;
                                                setFilter(newFilter);
                                                setFilteredArtworks(artworks.filter(a => {
                                                    if (a.onsale) return false;

                                                    if (a.artistName && a.artistName.toLowerCase().includes(newFilter.toLowerCase())) {
                                                        return true;
                                                    } else if (a.title && a.title.toLowerCase().includes(newFilter.toLowerCase())) {
                                                        return true;
                                                    }
                                                }));

                                                persistentModalState.filter = e.target.value;
                                            }}
                                        />
                                        <label className={styles['source-label']}>Sort by:</label>

                                        <select className={styles['sort-select']} onChange={e => sortBy(e.target.value)}>
                                            <option value="artistName">Artist</option>
                                            <option value="title">Title</option>
                                        </select>
                                    </div>}
                                {selectedExternalSource !== "artetra" &&
                                    <div>
                                        {/* <div className={styles['filter-section']}> */}
                                        {/* <label className={styles['source-label']}>Filter artworks</label>
                                <input type="text" className={styles['filter-input']} placeholder="Filter artworks..." /> */}

                                        <div
                                            className='row'
                                            data-tooltip={`Search the ${selectedExternalSource.charAt(0).toUpperCase() + selectedExternalSource.slice(1)} database using an artwork name.
                                                        * Artworks under specific copyright licences may not be shown`}
                                            // data-tooltip-pos="right"
                                            style={{ ["--tooltip-max-width" as any]: "400px" }}
                                        >
                                            <div className="col-6">
                                                <label className={styles['source-label']}>Filter artworks</label>
                                                <input className={styles['filter-input']} type="text" value={externalSourceFilter} onChange={(e) => setExternalSourceFilter(e.target.value)} />
                                            </div>
                                            <div className="col-6 mt-4">
                                                <button
                                                    className={`${styles.btn} ${styles['btn-primary']} `}
                                                    disabled={externalSourceFilter === "" || loadingExternalSource}
                                                    onClick={() => searchArtworks()}
                                                >
                                                    {loadingExternalSource ? "Searching..." : "Search"}
                                                </button>
                                                {/* {persistentModalState.cachedExternalArtworks[selectedExternalSource] && persistentModalState.cachedExternalArtworks[selectedExternalSource].length > 0 && (
                                                    <div className="text-muted small mt-1">
                                                        Showing {persistentModalState.cachedExternalArtworks[selectedExternalSource].length} cached results
                                                    </div>
                                                )} */}
                                            </div>
                                        </div>
                                        <div
                                            className="row mt-1"
                                            data-tooltip={`Find an artwork or a collection from ${selectedExternalSource.charAt(0).toUpperCase() + selectedExternalSource.slice(1)} using its URL address.
                                                        * Artworks under specific copyright licences may not be shown`}
                                            style={{ ["--tooltip-max-width" as any]: "400px" }}
                                        >
                                            <div className="col-6">
                                                <label className={styles['source-label']}>Search by link</label>
                                                <input className={styles['filter-input']} type="text" value={collectionSearch} onChange={(e) => setCollectionSearch(e.target.value)} />
                                            </div>
                                            <div className="col-6 mt-4">
                                                <button
                                                    className={`${styles.btn} ${styles['btn-primary']} `}
                                                    disabled={collectionSearch === "" || collectionSearchLoading}
                                                    style={{ ["--tooltip-max-width" as any]: "400px" }}
                                                    onClick={() => searchCollections()}>{collectionSearchLoading ? "Searching..." : "Search"}</button>
                                            </div>
                                        </div>
                                    </div>}
                                {canChoosePseudoMoMBatchPlacement && (
                                    <div className={styles['source-section']}>
                                        <label className={styles['source-label']}>Batch placement</label>
                                        <div className={styles['segmented-control']}>
                                            <button
                                                type="button"
                                                className={`${styles['segmented-button']} ${pseudoMoMBatchPlacementMode === "singleSlot" ? styles.active : ""}`}
                                                aria-pressed={pseudoMoMBatchPlacementMode === "singleSlot"}
                                                onClick={() => setPseudoMoMBatchPlacementMode("singleSlot")}
                                            >
                                                Single pillar
                                            </button>
                                            <button
                                                type="button"
                                                className={`${styles['segmented-button']} ${pseudoMoMBatchPlacementMode === "spreadSlots" ? styles.active : ""}`}
                                                aria-pressed={pseudoMoMBatchPlacementMode === "spreadSlots"}
                                                onClick={() => setPseudoMoMBatchPlacementMode("spreadSlots")}
                                            >
                                                Different pillars
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className={styles['objects-grid']}>
                                {filteredArtworks.map((artwork, i) => {
                                    const BASE_API = process.env.REACT_APP_BASE_API || '';
                                    const isImportable = isImportableExternalArtwork(artwork, spaceData);
                                    const isPlaceholderCandidate = isPlaceholderEligibleExternalArtwork(artwork, spaceData);

                                    return (
                                        <div
                                            className={`${styles['object-card']} ${isSameArtwork(selectedArtwork, artwork) ? styles.active : ""} `}
                                            key={artwork?._id || (artwork as any)?.id || i}
                                            onClick={() => {
                                                if (isImportable) onChangeArtwork(artwork);
                                            }}
                                            style={!isImportable ? { opacity: 0.55, cursor: "not-allowed" } : undefined}
                                            title={!isImportable ? getImportUnavailableReason(artwork) : undefined}
                                        >
                                            {!isImportable && <img src='/assets/blank-artwork.png' alt={artwork.title} className={styles['object-thumbnail']} />}
                                            {isImportable && artwork.type === "video" && <video src={artwork.artworkData.dataUrl} muted className={styles['object-thumbnail']} />}
                                            {isImportable && artwork.type === "image" && <img src={getProxiedImageUrl(artwork.artworkData.dataUrl, BASE_API)} alt={artwork.title} className={styles['object-thumbnail']} />}
                                            {isImportable && artwork.type === "sound" && <img src='/assets/speaker.png' alt={artwork.title} className={styles['object-thumbnail']} />}
                                            {isImportable && artwork.type === "3dmodel" && <img src='/assets/3d-model.png' alt={artwork.title} className={styles['object-thumbnail']} />}
                                            <div className={styles['object-info']}>
                                                <h3>{artwork.title}</h3>
                                                <p>{artwork.artistName}</p>
                                                {isPlaceholderCandidate && <p>{PLACEHOLDER_IMPORT_MESSAGE}</p>}
                                                {!isImportable && <p>{getImportUnavailableReason(artwork)}</p>}
                                            </div>
                                        </div>
                                    )
                                })}

                            </div>

                            <div className={styles['action-buttons']}>
                                <button
                                    onClick={putOnSale}
                                    className={`${styles.btn} ${styles['btn-primary']} `} data-tooltip="Add the selected artwork to the current placeholder"
                                    disabled={!selectedArtwork || loading || !isImportableExternalArtwork(selectedArtwork, spaceData)}
                                >

                                    {!selectedArtwork ? "Select an artwork" : !isImportableExternalArtwork(selectedArtwork, spaceData) ? (
                                        <>Unavailable for import</>
                                    ) : (loading && !selectedArtwork.onsale) ? (
                                        selectedArtwork.source && selectedArtwork.source.sourceName !== "artetra"
                                            ? <><span>+</span> Adding external artwork to placeholder...</>
                                            : <><span>+</span> Adding artwork to placeholder...</>
                                    ) : (
                                        selectedArtwork.source && selectedArtwork.source.sourceName !== "artetra"
                                            ? ((!selectedArtwork || !selectedArtwork.onsale) ? <><span>+</span> Add External Artwork</> : <><span>-</span> Remove External Artwork</>)
                                            : (!selectedArtwork || !selectedArtwork.onsale)
                                                ? <><span>+</span> Add to placeholder</>
                                                : <><span>-</span> Remove from placeholder</>
                                    )}
                                </button>
                                <button
                                    className={`${styles.btn} ${populating ? styles['btn-disabled'] : ""}
                                ${styles['btn-secondary']} `}
                                    data-tooltip="Add all the artworks in the list above (filters applied) to available placeholders in the gallery"
                                    onClick={autoPopulateHandler}
                                    disabled={populating || !bulkListAvailable}
                                >
                                    <span>≡</span> {populating ? "Adding multiple..." : `Add multiple(${filteredArtworks.length} selected)`}
                                </button>
                                {canUseArtworkRepositoryActions && <button
                                    className={`${styles.btn} ${styles['btn-secondary']} `}
                                    data-tooltip="Upload a new artwork to your Artetra artworks repository"
                                    onClick={createArtworkHandler}
                                >
                                    <span>✎</span> Create New Artwork
                                </button>}
                            </div>
                        </div>

                        <div className={`${styles['right-panel']} ${isPseudoMoMEditingSelectedArtwork ? styles['right-panel-editing'] : ""}`}>
                            <div className={styles['detail-header']}>
                                <h2 className={styles['detail-title']}>Artwork Details</h2>
                                <button onClick={handleHide} className={styles['close-btn']}>×</button>
                            </div>

                            <div className={`${styles['detail-body']} ${isPseudoMoMEditingSelectedArtwork ? styles['detail-body-editing'] : ""}`}>
                                <div className={styles['detail-content']}>
                                    {selectedArtwork && editingSelectedArtwork ? (
                                        <NewArtworkModal
                                            show={editingSelectedArtwork}
                                            inline
                                            hideInlineHeader={false}
                                            hideInlinePreview={!isPseudoMoMSpace}
                                            enableAssetReplacement
                                            allowedImportedMetadataKeys={isPseudoMoMSpace ? PSEUDO_MOM_EDIT_IMPORTED_METADATA_KEYS : undefined}
                                            pseudoMomLimitedMetadataEdit={isPseudoMoMSpace}
                                            inlinePresentation={isPseudoMoMSpace ? "modal" : "compact"}
                                            artworkToEdit={selectedArtwork}
                                            onCreatedHandler={onEditedArtworkSaved}
                                            onSuccess={successNotify}
                                            onFailed={failedNotify}
                                        />
                                    ) : selectedArtwork && (
                                        <>
                                            <div className={styles['artwork-preview-section']}>
                                                <div className={styles['artwork-preview']}>
                                                    {selectedArtwork.type === "video" && <video
                                                        src={selectedArtwork.artworkData.dataUrl}
                                                        style={{ width: "100%" }}
                                                        className={styles['artwork-image']}
                                                        muted
                                                        autoPlay
                                                        loop
                                                        controls
                                                    />}
                                                    {selectedArtwork.type === "image" && (
                                                        <ArtworkImageCarousel
                                                            images={
                                                                (selectedArtwork.source?.images && selectedArtwork.source.images.length > 0)
                                                                    ? selectedArtwork.source.images.map(img => getProxiedImageUrl(img, process.env.REACT_APP_BASE_API || ''))
                                                                    : [getProxiedImageUrl(selectedArtwork.artworkData.dataUrl, process.env.REACT_APP_BASE_API || '')]
                                                            }
                                                            renderSlide={(src) => <ZoomViewer src={src} className="" />}
                                                            alt={selectedArtwork.title ?? ''}
                                                            imageClassName={styles['artwork-image']}
                                                            carouselClassName={styles['artwork-carousel']}
                                                        />
                                                    )}
                                                    {selectedArtwork.type === "sound" && <audio
                                                        controls
                                                        className={styles['artwork-image']}
                                                        onPlay={() => onPauseBackgroundSound?.()}
                                                        onPause={() => onResumeBackgroundSound?.()}
                                                        onEnded={() => onResumeBackgroundSound?.()}
                                                    >
                                                        <source src={getProxiedSoundUrl(selectedArtwork.artworkData.dataUrl, process.env.REACT_APP_BASE_API || '')} />
                                                    </audio>}
                                                    {selectedArtwork.type === "3dmodel" && (
                                                        (selectedArtwork.source?.provider?.toLowerCase() === "sketchfab" && (selectedArtwork.source as any)?.thumbnailUrl) ? (
                                                            <img
                                                                src={(selectedArtwork.source as any).thumbnailUrl}
                                                                alt={selectedArtwork.title || "Sketchfab preview"}
                                                                style={{ width: "100%" }}
                                                                className={styles['artwork-image']}
                                                            />
                                                        ) : (
                                                            <div
                                                                ref={viewerRef}
                                                                style={{ width: "100%", flex: 1, minHeight: "200px", maxHeight: "350px" }}
                                                                className={styles['artwork-image']}
                                                            />
                                                        )
                                                    )}
                                                </div>

                                                {canUseArtworkRepositoryActions && <div className={styles['detail-actions']}>
                                                    <button
                                                        className={styles['btn-outline']}
                                                        data-tooltip="Use selected artwork as the thumbnail for this gallery. &#10;• Visitors will see this when browsing the list of galleries in the Exhibitions section"
                                                        onClick={setAsGalleryThumbnail}
                                                    >
                                                        <span>🖼</span> Set as Gallery Thumbnail
                                                    </button>
                                                    <button
                                                        className={styles['btn-outline']}
                                                        data-tooltip="Edit the details for the selected artwork"
                                                        onClick={() => setEditingSelectedArtwork(true)}
                                                    >
                                                        <span>✎</span> Edit Info
                                                    </button>
                                                </div>}
                                            </div>
                                            <div className={styles['artwork-metadata']}>
                                                <div className={styles['detail-actions']} style={{ marginBottom: "20px" }}>
                                                    <button
                                                        className={styles['btn-outline']}
                                                        data-tooltip={shareEmbedTooltip}
                                                        onClick={() => setShareEmbedOpen(value => !value)}
                                                        disabled={!isEmbedShareAvailable}
                                                    >
                                                        Share Embed
                                                    </button>
                                                </div>
                                                {shareEmbedOpen && (
                                                    <div className={styles['share-panel']}>
                                                        <div className={styles['share-panel-header']}>Embed Sharing</div>
                                                        <div className={styles['share-row']}>
                                                            <label className={styles['share-label']}>Embed Page</label>
                                                            <div className={styles['share-controls']}>
                                                                <input
                                                                    className={styles['share-input']}
                                                                    value={embedPageUrl}
                                                                    readOnly
                                                                    aria-label="Embed page URL"
                                                                />
                                                                <button
                                                                    className={styles['share-copy-btn']}
                                                                    onClick={() => copyTextToClipboard(embedPageUrl, "Embed page URL copied")}
                                                                    disabled={!embedPageUrl}
                                                                >
                                                                    Copy Embed Page URL
                                                                </button>
                                                                <button
                                                                    className={styles['share-copy-btn']}
                                                                    onClick={() => window.open(embedPageUrl, "_blank", "noopener,noreferrer")}
                                                                    disabled={!embedPageUrl}
                                                                >
                                                                    Open Embed Preview
                                                                </button>
                                                            </div>
                                                        </div>
                                                        <div className={styles['share-row']}>
                                                            <label className={styles['share-label']}>oEmbed JSON</label>
                                                            <div className={styles['share-controls']}>
                                                                <input
                                                                    className={styles['share-input']}
                                                                    value={oembedUrl}
                                                                    readOnly
                                                                    aria-label="oEmbed URL"
                                                                />
                                                                <button
                                                                    className={styles['share-copy-btn']}
                                                                    onClick={() => copyTextToClipboard(oembedUrl, "oEmbed URL copied")}
                                                                    disabled={!oembedUrl}
                                                                >
                                                                    Copy oEmbed URL
                                                                </button>
                                                            </div>
                                                        </div>
                                                        {oembedUnavailableReason && (
                                                            <div className={styles['share-note']}>{oembedUnavailableReason}</div>
                                                        )}
                                                    </div>
                                                )}
                                                <div className={styles['metadata-section']}>
                                                    <div className={styles['metadata-label']}>{isPseudoMoMSpace ? "CREATOR" : "ARTIST"}</div>
                                                    <div className={styles['metadata-value']}>{selectedArtwork.artistName}</div>
                                                </div>

                                                <div className={styles['metadata-section']}>
                                                    <div className={styles['metadata-label']}>TITLE</div>
                                                    <div className={styles['metadata-value']}>{selectedArtwork.title}</div>
                                                </div>

                                                <div className={styles['metadata-section']}>
                                                    {selectedArtwork.description && (
                                                        (typeof selectedArtwork.description === 'string'
                                                            ? selectedArtwork.description.trim() !== ''
                                                            : Array.isArray(selectedArtwork.description) && (selectedArtwork.description as string[]).some(d => (d || '').trim() !== ''))
                                                    ) && (
                                                            <>
                                                                <div className={styles['metadata-label']}>DESCRIPTION</div>
                                                                <div className={styles['metadata-value']}>
                                                                    {descriptionParagraphs(selectedArtwork.description)
                                                                        .map((desc, i) => <p key={i}>{desc}</p>)}
                                                                </div>
                                                            </>
                                                        )}
                                                </div>
                                                {
                                                    selectedArtwork.source &&
                                                    selectedProviderDisplay &&
                                                    <div className={styles['metadata-section']}>
                                                        <div className={styles['metadata-label']}>PROVIDER</div>
                                                        <div className={styles['metadata-value']}>{selectedProviderDisplay}</div>
                                                    </div>
                                                }
                                                {selectedArtwork.source && isPseudoMoMSpace && isEuropeanaSource(selectedArtwork.source) && (
                                                    <div className={styles['metadata-section']}>
                                                        <div className={styles['metadata-label']}>IMPORT SOURCE</div>
                                                        <div className={styles['metadata-value']}>{resolveEuropeanaImportSourceDisplay(selectedArtwork.source)}</div>
                                                    </div>
                                                )}

                                                {
                                                    selectedArtwork.source &&
                                                    selectedArtwork.source.rights &&
                                                    selectedArtwork.source.rights.length > 0 &&
                                                    selectedArtwork.source.rights[0] !== "" &&
                                                    <div className={styles['metadata-section']}>
                                                        <div className={styles['metadata-label']}>DISPLAY RIGHTS</div>
                                                        <div className={styles['metadata-value']}>{selectedArtwork.source.rights[0]}</div>
                                                    </div>
                                                }

                                                {
                                                    selectedArtwork.source &&
                                                    selectedArtwork.source.year &&
                                                    selectedArtwork.source.year !== "" &&
                                                    <div className={styles['metadata-section']}>
                                                        <div className={styles['metadata-label']}>YEAR</div>
                                                        <div className={styles['metadata-value']}>{selectedArtwork.source.year}</div>
                                                    </div>
                                                }
                                                <ImportedMetadataDetails
                                                    source={selectedArtwork.source}
                                                    artworkTitle={selectedArtwork.title}
                                                    artworkDescription={selectedArtwork.description}
                                                    artworkYear={(selectedArtwork.source as any)?.year || ""}
                                                    className={styles['metadata-section']}
                                                    labelClassName={styles['metadata-label']}
                                                    valueClassName={styles['metadata-value']}
                                                    pseudoMoMReadMode={isPseudoMoMSpace}
                                                    hideHeading={isPseudoMoMSpace}
                                                    contextEntities={selectedArtwork.edmProfile?.contextEntities}
                                                />

                                            </div>
                                        </>
                                    )}
                                </div>

                                {!isPseudoMoMEditingSelectedArtwork && <div
                                    className={styles['right-sidebar']}
                                    data-tooltip={selectedArtwork && !selectedArtwork.onsale ? "Add the artwork to the placeholder to activate the Display Options." : undefined}
                                >
                                    <h3 className={styles['sidebar-title']}>Display Options</h3>

                                    <fieldset
                                        disabled={controlsDisabled}
                                        style={{ border: 0, margin: 0, padding: 0 }}
                                    >
                                        {(selectedArtwork && selectedArtwork.onsale) && <><div className={styles['option-group']} data-tooltip="Change the size of your the current artwork.&#10;• Default height is set to 100 cm, equivalent to 1 metre in the space. &#10;• Changing the height will maintain the proportions of the artwork.">
                                            <label className={styles['option-label']}>Height (cm)</label>
                                            <div className={styles['scale-indicator']}>
                                                <input
                                                    type="text"
                                                    className={`${styles['option-input']} 
                                                    ${styles['scale-input']} `}
                                                    value={heightCm}
                                                    onChange={e => onChangeHeight(Number(e.target.value))}
                                                    placeholder="Height"
                                                />
                                            </div>
                                        </div>

                                            <div className={styles.divider}></div></>}

                                        {selectedArtwork && selectedArtwork.onsale && (selectedArtwork.type === "video" || selectedArtwork.type === "image") && <>
                                            <div className={styles['option-group']}>
                                                <div className={styles['checkbox-group']} data-tooltip="Untick this box if you would like to exclude the current artwork from the gallery lighting effects.">
                                                    <input
                                                        type="checkbox"
                                                        id="dynamic-lighting"
                                                        checked={affectedByLights}
                                                        onChange={e => onAffectedByLightsChange(e.target.checked)}
                                                    />
                                                    <label htmlFor="dynamic-lighting" className={styles['option-label']} style={{ margin: 0, cursor: "pointer" }}>Gallery lighting affects artwork</label>
                                                </div>
                                            </div>

                                            <div className={styles.divider}></div>
                                        </>}

                                        {selectedArtwork && selectedArtwork.onsale && (
                                            selectedArtwork.type === "video" || selectedArtwork.type === "image"
                                        ) && <div className={styles['option-group']} data-tooltip="Add, modify or remove the frame for the current artwork">
                                                <label className={styles['option-label']}>Frame Style</label>
                                                <div className={styles['filter-section']}>
                                                    <div>
                                                        <select className={styles['option-input']} value={frameType} onChange={e => onFrameTypeChange(e.target.value)}>
                                                            <option value="default">None</option>
                                                            <option value="frame1">Default frame</option>
                                                            <option value="frame1_pprt">Default frame with passepartout</option>
                                                        </select>
                                                    </div>


                                                </div>
                                                {frameType !== "default" && (
                                                    <div className={styles['color-picker-wrapper']} style={{ display: "inline-block", maxWidth: "260px" }}>
                                                        <span className={styles['color-label']}>Frame Color</span>
                                                        <div className={styles['color-swatch']} id="frameColorSwatch" style={{ background: frameColor }}>
                                                            <input
                                                                type="color"
                                                                id="frameColorPicker"
                                                                className={styles['color-input']}
                                                                value={frameColor}
                                                                onChange={e => onFrameColorChange(e.target.value)}
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                                {frameType !== "default" && <div className={styles['slider-wrapper']}>
                                                    <label className={styles['option-label']} style={{ marginTop: "15px" }}>Frame Size</label>
                                                    <input
                                                        type="range"
                                                        id="frameSize"
                                                        className={styles.slider}
                                                        min="0.1"
                                                        max="4.0"
                                                        step="0.1"
                                                        value={frameThickness}
                                                        onChange={e => onFrameThicknessChange(Number(e.target.value))}
                                                    />
                                                </div>}
                                                {frameType === "frame1_pprt" && <div className={styles['slider-wrapper']}>
                                                    <label className={styles['option-label']} style={{ marginTop: "15px" }}>Passepartout Size</label>
                                                    <input
                                                        type="range"
                                                        id="frameSize"
                                                        className={styles.slider}
                                                        min="0.01"
                                                        max="1"
                                                        step="0.01"
                                                        value={pasPartoutSize}
                                                        onChange={e => onPasPartoutSizeChange(Number(e.target.value))}
                                                    />
                                                </div>}
                                                <div className={styles.divider}></div>
                                            </div>}


                                        {selectedArtwork && selectedArtwork.onsale && (
                                            selectedArtwork.type === "3dmodel"
                                        ) && <div className={styles['option-group']} data-tooltip="Add, modify or remove the pedestal for the current 3D artwork.&#10;• Pedestals also prevent visitors from walking through the artwork.">
                                                <label className={styles['option-label']}>Pedestal Style</label>
                                                <div className={styles['filter-section']}>
                                                    <select className={styles['option-input']} value={pedestalType} onChange={e => onChangePedestalType(e.target.value)}>
                                                        <option>None</option>
                                                        <option>Cylinder</option>
                                                        <option>Hexagon</option>
                                                    </select>

                                                    <div className={styles['color-picker-wrapper']}>
                                                        <span className={styles['color-label']}>Pedestal Color</span>
                                                        <div className={styles['color-swatch']} id="pedestalColorSwatch" style={{ background: pedestalColor }}>
                                                            <input
                                                                type="color"
                                                                id="pedestalColorPicker"
                                                                className={styles['color-input']}
                                                                value={pedestalColor}
                                                                onChange={e => onPedestalColorChange(e.target.value)}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>}
                                    </fieldset>
                                    {canSetFlagship && (
                                        <div className={styles['option-group']}>
                                            <button
                                                className={styles['btn-outline']}
                                                data-tooltip="Make this artwork the first artwork shown on this pillar after the gallery reloads"
                                                onClick={setSelectedArtworkAsFlagship}
                                                disabled={selectedArtworkIsEffectiveFlagship || savingDisplayOptions}
                                            >
                                                Set flagship
                                            </button>
                                            {(selectedArtworkIsStagedFlagship || selectedArtworkIsPersistedFlagship) && (
                                                <span className={styles['flagship-status']}>
                                                    {selectedArtworkIsStagedFlagship ? "Flagship pending" : "Current flagship"}
                                                </span>
                                            )}
                                        </div>
                                    )}
                                    <button
                                        className={`${styles['apply-btn']} ${(!canApplyChanges || savingDisplayOptions) ? styles['disabled'] : ""} `}
                                        onClick={onSaveDisplayOptions}
                                        disabled={!canApplyChanges || savingDisplayOptions}
                                        data-tooltip={selectedArtwork && !selectedArtwork.onsale ? "Add this artwork to a placeholder to enable changes." : undefined}
                                        title={selectedArtwork && !selectedArtwork.onsale ? "Add this artwork to a placeholder to enable changes." : undefined}
                                    >
                                        {savingDisplayOptions ? "Saving..." : <><span>🖫</span> Apply Changes</>}
                                    </button>
                                </div>}
                            </div>
                        </div>
                    </div>
                </div>
            </Modal.Body>
        </Modal>
    </>)
}
